import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Receipt, LogOut, MapPin, Shield, Users, Clock, Award, Globe, Phone, Mail, Plane, CheckCircle, Star, Building, ArrowRight, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import ToolSidebar from '../components/ToolSidebar';
import '../styles/AboutPage.css';
import '../styles/HomePage.css';

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const AboutPage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const seo = useSEO('about', {
    title: 'About Us | Registan Tourism — Dubai Visa & Travel Agency Since 2020',
    description: 'Learn about Registan Tourism, a trusted visa and travel agency based in Dubai. We specialize in UAE family visa, freelance visa, golden visa, tourist visa, business setup, and guided tours across the Emirates.',
    keywords: 'about registan tourism, who is registan tours, dubai visa agency about, UAE travel company, trusted visa agency dubai'
  });
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const stats = [
    { number: '15+', label: t('aboutStatCountries') || 'Countries Served' },
    { number: '24/7', label: t('aboutStatSupport') || 'Customer Support' },
    { number: '99%', label: t('aboutStatApproval') || 'Approval Rate' }
  ];

  const services = [
    { icon: Plane, title: t('aboutSvcVisaTitle') || 'Dubai Tourist Visa', desc: t('aboutSvcVisaDesc') || '14, 30, and 60-day UAE tourist visas with fast processing in 3-5 business days.' },
    { icon: Globe, title: t('aboutSvcChangeTitle') || 'Visa Change Service', desc: t('aboutSvcChangeDesc') || 'Hassle-free visa change by car, bus, or airplane with border run coordination.' },
    { icon: Users, title: t('aboutSvcFamilyTitle') || 'Family Visa UAE', desc: t('aboutSvcFamilyDesc') || 'Sponsor your husband, wife, son, daughter, or mother for UAE family residence visa.' },
    { icon: Building, title: t('aboutSvcBusinessTitle') || 'Business Setup Dubai', desc: t('aboutSvcBusinessDesc') || 'Complete freelance visa and business license setup in Dubai free zones.' },
    { icon: Shield, title: t('aboutSvcConsultTitle') || 'Visa Consultation', desc: t('aboutSvcConsultDesc') || 'Expert guidance on UAE immigration, visa types, requirements, and documentation.' },
    { icon: Award, title: t('aboutSvcPremiumTitle') || 'Premium Service', desc: t('aboutSvcPremiumDesc') || 'Priority processing, VIP support, and dedicated case manager for urgent applications.' }
  ];

  const reasons = [
    { icon: Clock, title: t('aboutWhyFastTitle') || 'Fast Processing', desc: t('aboutWhyFastDesc') || 'Get your Dubai visa approved in 3-5 business days with our streamlined application system.' },
    { icon: Shield, title: t('aboutWhyTrustedTitle') || 'Trusted & Licensed', desc: t('aboutWhyTrustedDesc') || 'Authorized visa service provider registered in the UAE with years of immigration expertise.' },
    { icon: CheckCircle, title: t('aboutWhyApprovalTitle') || 'High Approval Rate', desc: t('aboutWhyApprovalDesc') || '99% visa approval rate thanks to our thorough document verification and compliance checks.' },
    { icon: Star, title: t('aboutWhyReviewsTitle') || '5-Star Reviews', desc: t('aboutWhyReviewsDesc') || 'Hundreds of satisfied customers from around the world trust Registan Tourism for their UAE visa.' },
    { icon: Phone, title: t('aboutWhyWhatsappTitle') || '24/7 WhatsApp Support', desc: t('aboutWhyWhatsappDesc') || 'Get instant help anytime via WhatsApp, live chat, or email from our multilingual support team.' },
    { icon: MapPin, title: t('aboutWhyLocalTitle') || 'Based in Dubai', desc: t('aboutWhyLocalDesc') || 'Local presence in Dubai with deep knowledge of UAE visa regulations and immigration policies.' }
  ];

  return (
    <div className="about-page">
      <Helmet>
        <link rel="canonical" href="https://registantours.com/about" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/about" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Header */}
      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/tours" className="dark-nav-link">Tours</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/about" className="dark-nav-link active">{t('about') || 'About'}</Link>
        </nav>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/about" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <div className="tool-page-content">

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg">
          <div className="about-glow about-glow-1"></div>
          <div className="about-glow about-glow-2"></div>
        </div>
        <div className="about-hero-content">
          <div className="about-badge">
            <MapPin size={14} />
            <span>{t('aboutBadge') || 'Based in Dubai, UAE'}</span>
          </div>
          <h1 data-testid="about-h1">
            {t('aboutHeroTitle') || 'Registan Tourism — Your Trusted Dubai Visa Service Provider & UAE Travel Agency'}
          </h1>
          <p className="about-hero-desc">
            {t('aboutHeroDesc') || 'We help thousands of travelers apply for a Dubai visa online, process UAE visa changes, and set up businesses in the Emirates. Fast, reliable, and affordable visa services with 24/7 customer support.'}
          </p>
          <div className="about-hero-actions">
            <button className="btn-primary about-cta-btn" onClick={() => navigate('/')}>
              {t('aboutApplyBtn') || 'Apply for Dubai Visa'} <ArrowRight size={18} />
            </button>
            <a href="https://wa.me/971569130707" target="_blank" rel="noopener noreferrer" className="btn-secondary about-whatsapp-btn">
              <Phone size={18} /> {t('aboutWhatsappBtn') || 'WhatsApp Us'}
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="about-stats">
        {stats.map((stat, i) => (
          <div key={i} className="about-stat-item" style={{ animationDelay: `${i * 0.1}s` }}>
            <span className="about-stat-number">{stat.number}</span>
            <span className="about-stat-label">{stat.label}</span>
          </div>
        ))}
      </section>

      {/* Who We Are */}
      <section className="about-section">
        <h2>{t('aboutWhoTitle') || 'Your Gateway to the UAE — Built on Trust, Powered by Experience'}</h2>
        <div className="about-text-grid">
          <div className="about-text-block">
            <p>{t('aboutWhoP1') || 'Registan Tourism is a professionally managed Dubai visa agency and UAE travel services provider helping travelers from over 15 countries obtain their UAE tourist visas, visit visas, and transit visas quickly and efficiently. Our team of experienced immigration consultants ensures every application is processed with the highest accuracy and compliance with UAE immigration laws.'}</p>
            <p>{t('aboutWhoP2') || 'Whether you need a 14-day Dubai visa, 30-day UAE tourist visa, or 60-day long-stay visa, we handle the entire process from document verification to visa stamping. Our smart online booking system uses passport OCR technology to automatically read your passport details, reducing errors and speeding up the application process.'}</p>
          </div>
          <div className="about-text-block">
            <p>{t('aboutWhoP3') || 'Beyond tourist visas, we specialize in visa change services in the UAE — also known as visa runs. Our visa change by car to Oman, visa change by bus, and visa change by airplane options provide flexible and convenient ways to renew your visa status without leaving the country for long.'}</p>
            <p>{t('aboutWhoP4') || 'We also offer family visa sponsorship in Dubai, freelance visa and business license setup in UAE free zones, and expert UAE immigration consultation. Our goal is to make visa services accessible, transparent, and stress-free for everyone visiting or living in the United Arab Emirates.'}</p>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="about-section about-services-section">
        <h2>{t('aboutServicesTitle') || 'Our Dubai Visa & UAE Travel Services'}</h2>
        <p className="about-section-subtitle">{t('aboutServicesSubtitle') || 'Comprehensive visa and immigration solutions for tourists, residents, and businesses in the UAE'}</p>
        <div className="about-services-grid">
          {services.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <div key={i} className="about-service-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="about-service-icon"><Icon size={24} /></div>
                <h3>{svc.title}</h3>
                <p>{svc.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="about-section">
        <h2>{t('aboutWhyTitle') || 'Why Choose Registan Tourism for Your Dubai Visa?'}</h2>
        <p className="about-section-subtitle">{t('aboutWhySubtitle') || 'Trusted by hundreds of travelers worldwide for reliable UAE visa services'}</p>
        <div className="about-reasons-grid">
          {reasons.map((r, i) => {
            const Icon = r.icon;
            return (
              <div key={i} className="about-reason-card" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="about-reason-icon"><Icon size={20} /></div>
                <div>
                  <h3>{r.title}</h3>
                  <p>{r.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta-section">
        <div className="about-cta-card">
          <h2>{t('aboutCtaTitle') || 'Ready to Apply for Your Dubai Visa?'}</h2>
          <p>{t('aboutCtaDesc') || 'Start your UAE visa application today. Fast processing, affordable prices, and dedicated support from our Dubai-based team.'}</p>
          <div className="about-cta-buttons">
            <button className="btn-primary about-cta-main" onClick={() => navigate('/')}>
              {t('aboutCtaBtn') || 'Apply Now'} <ArrowRight size={18} />
            </button>
            <a href="mailto:support@registantours.com" className="about-cta-email">
              <Mail size={16} /> support@registantours.com
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default AboutPage;
